import SiteLayout from '@/components/site/SiteLayout';
import Link from 'next/link';
import Image from 'next/image';
import {
  BriefcaseMedical,
  CalendarDays,
  Coins,
  Lightbulb,
  Rocket,
  Route,
  ShieldCheck,
  Stethoscope,
  UserRound,
  WalletCards,
} from 'lucide-react';

const journeySteps = [
  {
    eyebrow: 'The Reality',
    title: 'Residency Debt',
    body: 'Facing mounting student loans during residency, the need for flexible shifts became undeniable.',
    icon: Stethoscope,
  },
  {
    title: 'The Inefficient Network',
    body: 'Hospitals desperate for coverage while physicians struggled to find shifts.',
    icon: BriefcaseMedical,
  },
  {
    title: 'The Idea Sparks',
    body: 'A direct-to-facility platform eliminating the middleman.',
    icon: Lightbulb,
  },
  {
    title: 'Building the Tech',
    body: 'Late nights coding a seamless, compliant interface for medical professionals.',
    icon: Route,
  },
  {
    title: 'Growth and Impact',
    body: 'Connecting thousands of clinicians with facilities nationwide.',
    icon: Rocket,
    featured: true,
  },
];

const impactCards = [
  {
    title: 'Maximize Earnings',
    body: 'Pick up one extra shift or build a fuller schedule during a research year, gap year, or elective — with no long-term commitment.',
    icon: Coins,
  },
  {
    title: 'Flexible Scheduling',
    body: 'Find nearby opportunities from your phone based on your location, availability, and preferences.',
    icon: CalendarDays,
  },
  {
    title: 'Streamlined Credentialing',
    body: 'Complete onboarding through the app, with licensing support available for select opportunities.',
    icon: ShieldCheck,
  },
  {
    title: 'Direct Facility Access',
    body: 'Connect with trusted facilities that fit your schedule, then show up, work, and get paid.',
    icon: WalletCards,
  },
];

const FounderPage = () => {
  return (
    <SiteLayout
      title='Founder Story | Why DocMoonlight Built Physician Coverage Differently'
      description='Learn how Dr. Ramez Morcos built DocMoonlight to solve real imaging center coverage gaps with flexible onsite, virtual, and hybrid physician support.'
      keywords={[
        'DocMoonlight founder',
        'Ramez Morcos',
        'physician coverage',
        'imaging center staffing',
      ]}
    >
      <section className='px-4 pb-10 pt-8 sm:px-6 lg:px-8 lg:pt-14'>
        <div className='mx-auto max-w-7xl'>
          <div className='overflow-hidden rounded-[1.75rem] border border-slate-200 bg-[radial-gradient(circle_at_top_right,_rgba(245,158,11,0.14),_transparent_20%),radial-gradient(circle_at_bottom_left,_rgba(59,130,246,0.08),_transparent_28%),linear-gradient(180deg,#ffffff_0%,#fbfdff_100%)] px-4 py-6 shadow-[0_28px_80px_rgba(15,23,42,0.08)] sm:rounded-[2.4rem] sm:px-6 sm:py-8 md:px-10 md:py-10'>
            <div className='grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.88fr)] lg:gap-10'>
              <div className='min-w-0 text-center lg:text-left'>
                <div className='inline-flex max-w-full items-center gap-2 rounded-full border border-[#f3d39b] bg-white px-3 py-2 text-[0.64rem] font-black uppercase tracking-[0.16em] text-[#d18a00] shadow-[0_10px_24px_rgba(15,23,42,0.06)] sm:px-4 sm:text-[0.7rem] sm:tracking-[0.2em]'>
                  <span className='h-2 w-2 rounded-full bg-[#f59e0b]' />
                  Founder Story
                </div>
                <h1 className='mx-auto mt-5 max-w-4xl text-[2.15rem] font-black leading-[0.98] tracking-[-0.05em] text-[#10284d] sm:text-[2.65rem] md:text-[3.4rem] lg:mx-0'>
                  Built by physicians who lived the problem.
                </h1>

                <div className='mx-auto mt-6 max-w-3xl rounded-[1.35rem] border border-slate-200 bg-white/90 p-4 text-left shadow-[0_16px_36px_rgba(15,23,42,0.06)] sm:rounded-[1.75rem] sm:p-5 lg:mx-0'>
                  <div className='flex gap-3 sm:gap-4'>
                    <div className='w-1 shrink-0 rounded-full bg-[linear-gradient(180deg,#f59e0b_0%,#fbbf24_100%)]' />
                    <div className='min-w-0'>
                      <p className='text-[0.96rem] italic leading-7 text-slate-700 md:text-[1.08rem] md:leading-8'>
                        &ldquo;I lived the pressure of training and the
                        challenge of finding fair moonlighting opportunities.
                        DocMoonlight was built to make access easier for doctors
                        and coverage simpler for facilities.&rdquo;
                      </p>
                      <div className='mt-5 flex items-center gap-3'>
                        <Image
                          src='/images/founder.png'
                          alt='Ramez Morcos'
                          width={44}
                          height={44}
                          sizes='44px'
                          className='h-11 w-11 rounded-full object-cover shadow-sm'
                        />
                        <div>
                          <div className='text-sm font-black text-slate-950'>
                            Ramez Morcos, MD, MBA
                          </div>
                          <div className='text-sm text-slate-500'>
                            Founder, CEO
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='mt-7 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start'>
                  <Link
                    href='/residents'
                    className='inline-flex min-h-[54px] items-center justify-center rounded-full bg-[linear-gradient(90deg,#132b50_0%,#0f172a_100%)] px-6 text-[0.95rem] font-black text-white shadow-[0_18px_36px_rgba(15,23,42,0.18)] transition hover:translate-y-[-1px] sm:min-h-[58px] sm:px-7 sm:text-[0.98rem]'
                  >
                    Join DocMoonlight
                    <span className='ml-3 text-lg'>&rarr;</span>
                  </Link>
                  <Link
                    href='/about'
                    className='inline-flex min-h-[54px] items-center justify-center rounded-full border border-slate-200 bg-white px-6 text-[0.95rem] font-black text-[#12315f] shadow-[0_10px_24px_rgba(15,23,42,0.06)] transition hover:bg-slate-50 sm:min-h-[58px] sm:px-7 sm:text-[0.98rem]'
                  >
                    Learn About Our Mission
                  </Link>
                </div>
              </div>

              <div className='min-w-0 rounded-[1.45rem] border border-slate-200 bg-white p-2 shadow-[0_26px_60px_rgba(15,23,42,0.1)] sm:rounded-[2rem] sm:p-4'>
                <div className='overflow-hidden rounded-[1.2rem] bg-[linear-gradient(180deg,#13284b_0%,#0f1d37_100%)] sm:rounded-[1.6rem]'>
                  <div className='flex items-center justify-between px-4 py-3'></div>
                  <div className='px-3 pb-3 sm:px-4 sm:pb-4 md:px-5 md:pb-5'>
                    <div className='overflow-hidden rounded-[1rem] bg-[#0f1d37] sm:rounded-[1.35rem]'>
                      <div className='aspect-video w-full'>
                        <iframe
                          className='h-full w-full'
                          src='https://www.youtube.com/embed/MX5dIHqEAYE'
                          title='Founder message placeholder video'
                          loading='lazy'
                          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                          referrerPolicy='strict-origin-when-cross-origin'
                          allowFullScreen
                        />
                      </div>
                    </div>
                    <div className='px-1 pb-2 pt-4 text-white sm:pt-5'>
                      <div className='text-[1.2rem] font-black tracking-[-0.03em] sm:text-[1.45rem]'>
                        A Message from Dr. Morcos
                      </div>
                      <p className='mt-2 text-[0.9rem] leading-6 text-white/70 sm:text-[0.96rem] sm:leading-7'>
                        Discover the vision behind DocMoonlight and why the
                        platform was built around physician reality, not
                        staffing theory.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='px-4 py-8 sm:px-6 lg:px-8'>
        <div className='journey-panel mx-auto max-w-7xl rounded-[2.2rem] border border-slate-200 bg-white px-5 py-10 shadow-[0_24px_70px_rgba(15,23,42,0.08)] md:px-8 md:py-12 lg:px-10'>
          <div className='mx-auto max-w-3xl text-center'>
            <h2 className='text-[2rem] font-black leading-[0.96] tracking-[-0.05em] text-[#10284d] md:text-[3.15rem]'>
              Our Journey
            </h2>
            <p className='mx-auto mt-4 max-w-[42rem] text-[0.98rem] leading-8 text-slate-500 md:text-[1.08rem]'>
              From residency struggles to revolutionizing medical staffing,
              here&apos;s how DocMoonlight came to be.
            </p>
          </div>

          <div className='mt-12 hidden items-center px-6 lg:flex'>
            {journeySteps.map((step, index) => {
              const Icon = step.icon;
              const isLast = index === journeySteps.length - 1;

              return (
                <div
                  key={step.title}
                  className={`journey-step-group flex items-center ${isLast ? 'flex-none' : 'flex-1'}`}
                >
                  <div
                    className={`journey-timeline-node journey-timeline-node-${index} inline-flex h-14 w-14 items-center justify-center rounded-full border-[3px] ${
                      step.featured
                        ? 'border-[#13284b] bg-[#13284b] text-white shadow-[0_14px_28px_rgba(15,23,42,0.18)]'
                        : 'border-[#e4b23c] bg-white text-[#13284b] shadow-[0_12px_24px_rgba(15,23,42,0.08)]'
                    }`}
                  >
                    <Icon className='h-5 w-5' />
                  </div>
                  {!isLast && (
                    <div className='h-[2px] flex-1 bg-[linear-gradient(90deg,#edd389_0%,#e4b23c_50%,#edd389_100%)]' />
                  )}
                </div>
              );
            })}
          </div>

          <div className='mt-6 grid gap-4 sm:grid-cols-2 lg:mt-10 lg:grid-cols-5 lg:items-stretch'>
            {journeySteps.map((step, index) => {
              const Icon = step.icon;

              return (
                <article
                  key={step.title}
                  className={`journey-card-motion journey-card-motion-${index} relative flex flex-col overflow-hidden rounded-[1.45rem] border p-5 shadow-[0_12px_30px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-3 hover:border-[#f59e0b] hover:shadow-[0_28px_70px_rgba(245,158,11,0.22)] lg:min-h-[270px] ${
                    step.featured
                      ? 'border-[#13284b] bg-[linear-gradient(180deg,#13284b_0%,#0f1d37_100%)] text-white hover:bg-[linear-gradient(180deg,#183765_0%,#0f1d37_100%)]'
                      : 'border-slate-200 bg-white hover:bg-[linear-gradient(180deg,#fffaf0_0%,#ffffff_72%)]'
                  }`}
                >
                  {/* Mobile: step number + icon row */}
                  <div className='mb-3 flex items-center gap-2.5 lg:hidden'>
                    <span
                      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[0.7rem] font-black ${
                        step.featured
                          ? 'bg-[#f3d79a] text-[#13284b]'
                          : 'bg-[#13284b] text-white'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span
                      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${
                        step.featured
                          ? 'border-white/30 text-[#f3d79a]'
                          : 'border-[#e4b23c] text-[#13284b]'
                      }`}
                    >
                      <Icon className='h-4 w-4' />
                    </span>
                    {index < journeySteps.length - 1 && (
                      <div
                        className={`h-px flex-1 ${
                          step.featured ? 'bg-white/20' : 'bg-[#e4b23c]/50'
                        }`}
                      />
                    )}
                  </div>

                  <div
                    className={`text-[0.68rem] font-black uppercase tracking-[0.18em] ${
                      step.featured ? 'text-[#f3d79a]' : 'text-[#d18a00]'
                    }`}
                  >
                    {step.eyebrow || ''}
                  </div>
                  <h3
                    className={`mt-2 text-[1.02rem] font-black leading-7 ${
                      step.featured ? 'text-white' : 'text-slate-950'
                    }`}
                  >
                    {step.title}
                  </h3>
                  <p
                    className={`mt-3 text-[0.92rem] leading-8 ${
                      step.featured ? 'text-white/74' : 'text-slate-600'
                    }`}
                  >
                    {step.body}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className='px-4 py-10 sm:px-6 lg:px-8'>
        <div className='mx-auto max-w-7xl'>
          <div className='mx-auto max-w-3xl text-center'>
            <h2 className='text-[2rem] font-black leading-[0.96] tracking-[-0.05em] text-[#10284d] md:text-[3.15rem]'>
              What DocMoonlight Helps With
            </h2>
            <p className='mx-auto mt-4 max-w-3xl text-[0.98rem] leading-8 text-slate-500 md:text-[1.08rem]'>
              Empowering physicians to take control of their careers while
              providing facilities with reliable, high-quality coverage.
            </p>
          </div>

          <div className='mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4'>
            {impactCards.map((card) => {
              const Icon = card.icon;

              return (
                <article
                  key={card.title}
                  className='impact-card-motion relative min-h-[332px] overflow-hidden rounded-[1.8rem] border border-slate-200 bg-white p-6 shadow-[0_22px_54px_rgba(15,23,42,0.1)]'
                >
                  <div className='absolute inset-x-0 top-0 h-1.5 bg-[linear-gradient(90deg,#f59e0b_0%,#fbbf24_100%)]' />
                  <div className='flex flex-col items-center gap-4 text-center'>
                    <div className='relative inline-flex h-[4.6rem] w-[4.6rem] shrink-0 items-center justify-center rounded-[1.25rem] bg-[#101a35] text-[#f59e0b] shadow-[0_14px_28px_rgba(15,23,42,0.22)]'>
                      <Icon className='h-6 w-6' />
                      <span className='absolute -right-1 top-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-[#f59e0b]' />
                    </div>
                  </div>
                  <h3 className='mt-10 whitespace-nowrap text-center text-[1.18rem] font-black leading-7 tracking-tight text-[#111b3a] 2xl:text-[1.28rem]'>
                    {card.title}
                  </h3>
                  <p className='mx-auto mt-5 max-w-[29ch] text-center text-[0.95rem] font-semibold leading-8 text-slate-600'>
                    {card.body}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default FounderPage;
